/**
 * ============================================================================
 * [P3-04-E] URLCard - URL 카드 컴포넌트
 *
 * 기능:
 * - URL 정보 표시 (이름, URL, 카테고리, 아이콘)
 * - 드래그 핸들
 * - URL 열기 (새 탭)
 * - 수정 기능
 * - 삭제 기능
 * ============================================================================
 */

// ============================================================================
// 1. Imports
// ============================================================================
import { memo, useCallback } from "react";
import type { URLLink } from "@/types";
import styles from "./URLLink.module.css";

// ============================================================================
// 2. Types
// ============================================================================
interface URLCardProps {
  url: URLLink;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  dragHandleProps?: Record<string, unknown>;
}

// ============================================================================
// 3. URLCard Component (memo로 최적화)
// ============================================================================
export const URLCard = memo(function URLCard({
  url,
  onEdit,
  onDelete,
  dragHandleProps,
}: URLCardProps) {
  // ========================================================================
  // 3-1. URL 열기 핸들러
  // ========================================================================
  const handleOpen = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      window.open(url.url, "_blank", "noopener,noreferrer");
    },
    [url.url],
  );

  // ========================================================================
  // 3-2. 수정 핸들러
  // ========================================================================
  const handleEdit = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onEdit(url.id);
    },
    [url.id, onEdit],
  );

  // ========================================================================
  // 3-3. 삭제 핸들러
  // ========================================================================
  const handleDelete = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onDelete(url.id);
    },
    [url.id, onDelete],
  );

  // ========================================================================
  // 3-4. Render
  // ========================================================================
  return (
    <div className={styles.urlCard}>
      {/* ================================================================
          드래그 핸들
          ================================================================ */}
      <div
        className={styles.dragHandle}
        {...dragHandleProps}
        title="드래그하여 순서 변경"
      >
        ⠿
      </div>

      {/* ================================================================
          아이콘
          ================================================================ */}
      <div className={styles.urlIcon}>{url.icon || "🔗"}</div>

      {/* ================================================================
          내용
          ================================================================ */}
      <div className={styles.urlContent}>
        <h4>{url.name || url.title || "이름 없음"}</h4>
        {url.description && (
          <span className={styles.urlDescription}>{url.description}</span>
        )}
        <span className={styles.urlText}>{url.url}</span>
      </div>

      {/* ================================================================
          카테고리 뱃지
          ================================================================ */}
      {url.category && <span className={styles.category}>{url.category}</span>}

      {/* ================================================================
          액션 버튼 그룹 (열기, 수정, 삭제)
          ================================================================ */}
      <div className={styles.cardActions}>
        <button
          className={styles.openBtn}
          onClick={handleOpen}
          title="새 탭에서 열기"
        >
          🚀 열기
        </button>
        <button className={styles.editBtn} onClick={handleEdit} title="수정">
          ✏️
        </button>
        <button
          className={styles.deleteBtn}
          onClick={handleDelete}
          title="삭제"
          aria-label={`${url.name || url.title} 삭제`}
        >
          🗑️
        </button>
      </div>
    </div>
  );
});

export default URLCard;
